import type { CSMSDK } from '@atlassian/customer-service-management-widget-sdk-types/CSMSDK';
import type { CSMSDKQueue } from '@atlassian/customer-service-management-widget-sdk-types/CSMSDKQueue';

import { getSDKSrc } from './get-sdk-src';
import { getPreconnectHref } from './utils';

declare global {
	interface Window {
		CSM?: CSMSDK | CSMSDKQueue;
	}
}

let promise: Promise<CSMSDK> | null = null;

const NOOP = () => {};

type InitCSMSDK = {
	site: string;
	siteId?: string;
	cloudId?: string;
	widgetId?: string;
	debug?: ({ message }: { message: string }) => void;
};

export const loadCSMSDK = ({
	site,
	siteId,
	cloudId,
	widgetId,
	debug = NOOP,
}: InitCSMSDK): Promise<CSMSDK> => {
	if (!promise) {
		promise = new Promise<CSMSDK>((resolve, reject) => {
			if (typeof window === 'undefined') {
				debug({
					message: '[SDKLoader]: window is undefined',
				});
				reject(new Error('Failed to load CSM SDK: window is undefined'));
			}

			if (window.CSM && !('__q__' in window.CSM)) {
				debug({
					message: '[SDKLoader]: window.CSM already exists',
				});
				resolve(window.CSM);
			}

			const script = document.createElement('script');
			script.src = getSDKSrc({ site, siteId, cloudId, widgetId });
			script.setAttribute('async', '');
			script.type = 'text/javascript';
			script.onerror = () => {
				reject(new Error('Failed to load CSM SDK script: onerror'));
			};
			script.onload = () => {
				debug({
					message: '[SDKLoader]: script onload called',
				});

				if (window.CSM && !('__q__' in window.CSM)) {
					debug({
						message: '[SDKLoader]: CSM SDK found after script onload',
					});
					resolve(window.CSM);
				} else {
					debug({
						message: '[SDKLoader]: CSM SDK not found after script onload',
					});
					reject(new Error('Failed to load CSM SDK: window.CSM is undefined'));
				}
			};

			debug({
				message: '[SDKLoader]: CSM SDK appended to body',
			});

			document.body.appendChild(script);

			/**
			 * Preconnect to initialise the connection ahead of loading any iframe
			 * which will load static assets from the JCS widget domain.
			 */
			const link = document.createElement('link');
			const linkHref = getPreconnectHref(site);
			link.rel = 'preconnect';
			link.href = linkHref;
			link.crossOrigin = 'anonymous';
			document.head.appendChild(link);
			debug({
				message: `[SDKLoader]: CSM SDK preconnecting to ${linkHref}`,
			});
		});
	}

	return promise;
};
